import { Component, EventEmitter, Input, OnInit, Output, Optional, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { StorageService } from 'src/app/share/services/storage-service/storage.service';
import { StorageKeys } from 'src/app/share/enums/storage.enum';

function createPageRangeValidator(maxPages: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = (control.value || '').trim();
    if (!value) return null;
    const parts = value.split(',').map(s => s.trim());
    for (const part of parts) {
      const rangeMatch = /^(\d+)-(\d+)$/.exec(part);
      const singleMatch = /^(\d+)$/.exec(part);
      if (!rangeMatch && !singleMatch) return { rangeFormat: true };
      if (rangeMatch) {
        if (Number(rangeMatch[1]) < 1) return { rangeMin: true };
        if (Number(rangeMatch[1]) > Number(rangeMatch[2])) return { rangeOrder: true };
        if (maxPages && Number(rangeMatch[2]) > maxPages) return { rangeExceeds: true };
      }
      if (singleMatch) {
        if (Number(singleMatch[1]) < 1) return { rangeMin: true };
        if (maxPages && Number(singleMatch[1]) > maxPages) return { rangeExceeds: true };
      }
    }
    return null;
  };
}

function createPageCountValidator(maxPages: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const val = Number(control.value);
    if (!val) return null;
    if (val < 1) return { min: true };
    if (maxPages && val > maxPages) return { pageCountExceeds: true };
    return null;
  };
}

function createFileSizeValidator(maxBytes: number, unit: 'MB' | 'GB'): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const val = Number(control.value);
    if (!val) return null;
    if (val <= 0) return { min: true };
    if (maxBytes) {
      const divisor = unit === 'GB' ? 1024 ** 3 : 1024 ** 2;
      if (val > maxBytes / divisor) return { sizeExceeds: true };
    }
    return null;
  };
}

@Component({
  selector: 'app-pdf-download-options',
  templateUrl: './pdf-download-options.html',
  styleUrl: './pdf-download-options.scss',
  standalone: false
})
export class PdfDownloadOptions implements OnInit {
  @Input() fileName: string = '';
  @Input() totalPages: number = 0;
  @Input() fileSizeBytes: number = 0;
  @Input() allowedDownloadOptions: string[] | null = null;
  @Output() downloadSelected = new EventEmitter<any>();
  @Output() closePopup = new EventEmitter<void>();

  downloadForm: FormGroup;
  private readonly prefKeyMap: Record<string, string> = {
    RANGE:      'pageRange',
    PAGE_COUNT: 'pageCount',
    FILE_SIZE:  'fileSize',
    COMPRESS:   'compression',
  };

  get fileSizeUnit(): 'MB' | 'GB' {
    return this.fileSizeBytes >= 1024 ** 3 ? 'GB' : 'MB';
  }

  get maxFileSizeDisplay(): string {
    if (!this.fileSizeBytes) return '';
    const divisor = this.fileSizeUnit === 'GB' ? 1024 ** 3 : 1024 ** 2;
    return `${(this.fileSizeBytes / divisor).toFixed(2)} ${this.fileSizeUnit}`;
  }

  private readonly allOptions = [
    { label: 'Full Document',       value: 'FULL',       description: 'Download the entire PDF as a single file.' },
    { label: 'Page Range',          value: 'RANGE',      description: 'Download specific pages or page ranges.' },
    { label: 'Split by Page Count', value: 'PAGE_COUNT', description: 'Split into files with a fixed number of pages.' },
    { label: 'Split by File Size',  value: 'FILE_SIZE',  description: 'Split into files up to a maximum size.' },
    { label: 'Compress PDF',        value: 'COMPRESS',   description: 'Reduce file size using server-side compression.' },
  ];

  readonly compressionLevels = [
    { label: 'Extreme Compression', value: 'Extreme Compression',     hint: 'Smallest file size, may reduce quality.' },
    { label: 'Recommended',         value: 'Recommended Compression', hint: 'Best balance of size and quality.' },
    { label: 'Less Compression',    value: 'Less Compression',        hint: 'Minimal compression, preserves quality.' },
  ];

  get hasPageCount(): boolean {
    return !!this.totalPages && this.totalPages > 0;
  }

  get hasFileSize(): boolean {
    return !!this.fileSizeBytes && this.fileSizeBytes > 0;
  }

  isAllowedByPref(optionValue: string): boolean {
    if (optionValue === 'FULL') return true;
    if (!this.allowedDownloadOptions) return true;           // no restriction — show all
    const prefKey = this.prefKeyMap[optionValue];
    return !!prefKey && this.allowedDownloadOptions.includes(prefKey);
  }

  get options() {
    return this.allOptions.filter(opt => {
      if (!this.isAllowedByPref(opt.value)) return false;
      if (opt.value === 'FULL') return true;
      if (opt.value === 'RANGE' || opt.value === 'PAGE_COUNT') return this.hasPageCount;
      if (opt.value === 'FILE_SIZE') return this.hasFileSize;
      return true;
    });
  }

  get hasExtraOptions(): boolean {
    return this.options.some(o => o.value !== 'FULL');
  }

  get actionButtonLabel(): string {
    return this.downloadForm.get('downloadType')?.value === 'COMPRESS' ? 'Compress' : 'Download';
  }

  constructor(
    @Optional() public readonly activeModal: NgbActiveModal,
    private readonly cdr: ChangeDetectorRef,
    private readonly storageService: StorageService
  ) {
    this.downloadForm = new FormGroup({
      downloadType:     new FormControl('FULL', Validators.required),
      pageRange:        new FormControl(''),
      pageCount:        new FormControl(''),
      fileSize:         new FormControl(''),
      compressionLevel: new FormControl('Recommended Compression'),
    });
  }

  ngOnInit() {
    if (this.allowedDownloadOptions === null) {
      const prefs = this.storageService.getDataFromLocalStorage(StorageKeys.PREFERENCES);
      const downloadOptions = prefs?.documentVault?.downloadOptions;
      if (downloadOptions?.enabled && Array.isArray(downloadOptions?.values)) {
        this.allowedDownloadOptions = downloadOptions.values;
      } else {
        this.allowedDownloadOptions = null;
      }
    }
    const current = this.downloadForm.get('downloadType')?.value;
    if (!this.options.some(o => o.value === current)) {
      this.downloadForm.get('downloadType')?.setValue('FULL');
    }

    this.downloadForm.get('downloadType')?.valueChanges.subscribe(type => {
      this.clearValidators();
      if (type === 'RANGE') {
        this.downloadForm.get('pageRange')?.setValidators([
          Validators.required,
          createPageRangeValidator(this.totalPages),
        ]);
      } else if (type === 'PAGE_COUNT') {
        this.downloadForm.get('pageCount')?.setValidators([
          Validators.required,
          createPageCountValidator(this.totalPages),
        ]);
      } else if (type === 'FILE_SIZE') {
        this.downloadForm.get('fileSize')?.setValidators([
          Validators.required,
          createFileSizeValidator(this.fileSizeBytes, this.fileSizeUnit),
        ]);
      } else if (type === 'COMPRESS') {
        this.downloadForm.get('compressionLevel')?.setValidators([Validators.required]);
        if (!this.downloadForm.get('compressionLevel')?.value) {
          this.downloadForm.get('compressionLevel')?.setValue('Recommended Compression');
        }
      }
      ['pageRange', 'pageCount', 'fileSize', 'compressionLevel'].forEach(f =>
        this.downloadForm.get(f)?.updateValueAndValidity({ emitEvent: false })
      );
      this.downloadForm.updateValueAndValidity();
      this.cdr.detectChanges();
    });

    this.downloadForm.statusChanges.subscribe(() => this.cdr.detectChanges());
  }

  clearValidators() {
    ['pageRange', 'pageCount', 'fileSize'].forEach(field => {
      const control = this.downloadForm.get(field);
      control?.clearValidators();
      control?.setValue('');
      control?.setErrors(null);
    });
    const compLevel = this.downloadForm.get('compressionLevel');
    compLevel?.clearValidators();
    compLevel?.setErrors(null);
  }

  close() {
    if (this.activeModal) this.activeModal.close();
    this.closePopup.emit();
  }

  download() {
    if (this.downloadForm.invalid) return;
    const form = this.downloadForm.value;
    let payload: any = {};
    if (form.downloadType === 'FILE_SIZE') {
      payload = { splitBySize: form.fileSize, unit: this.fileSizeUnit };
    } else if (form.downloadType === 'RANGE') {
      payload = { pageRange: (form.pageRange as string).replaceAll(/\s/g, '') };
    } else if (form.downloadType === 'PAGE_COUNT') {
      payload = { splitByPage: form.pageCount };
    } else if (form.downloadType === 'COMPRESS') {
      payload = { isCompress: true, compressionLevel: form.compressionLevel };
    }
    if (this.activeModal) this.activeModal.close(payload);
    this.downloadSelected.emit(payload);
  }
}
